import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Category } from "./../../types/productType";
import { api } from "../../config/Api";
import { CategoryReq } from "../../types/dealType";

type CreateCatsResp = { success: boolean; message?: string };
export const createCategories = createAsyncThunk<
  { success: boolean },
  { level1Categories: CategoryReq[]; level2Categories: CategoryReq[]; level3Categories: CategoryReq[] }
>('homeCategory/createCategories', async (payload, { rejectWithValue }) => {
  try {
    // If your api returns data-only, resp is { success: boolean }
    // If not, resp is AxiosResponse<{ success: boolean }>
    const resp = await api.post('/categories/create', payload);

    // Read success robustly from either shape
    const success =
      typeof resp === 'object' && 'success' in (resp as any)
        ? !!(resp as any).success
        : !!(resp as any)?.data?.success;

    return { success };
  } catch (err: any) {
    // return (do not throw after rejectWithValue)
    const msg =
      err?.response?.data?.message ?? err?.message ?? 'Failed to create categories';
    return rejectWithValue(msg);
  }
});


export const getCategoryByCategoryId = createAsyncThunk<Category[], string>(
  "homeCategory/getCategoryByCategoryId",
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/categories/${categoryId}`);
      return response.data;
    } catch (error: any) {
      // Handle the error and return it to be used in rejected action
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create home categories";
    
      return rejectWithValue(errorMessage);
    }
  }
);
interface CategoriesState {
  createdCategories: boolean;
  categories: Category[];
  loading: boolean;
  error: string | null;
}

const initialState: CategoriesState = {
  categories: [],
  createdCategories: false,
  loading: false,
  error: null,
};

const categorySlice = createSlice({
  name: "categories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Handle the pending state for updateHomeCategory
    builder.addCase(createCategories.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.createdCategories = false;
    });

    // Handle the fulfilled state for createCategories
    builder.addCase(
      createCategories.fulfilled,
      (state, action: PayloadAction<CreateCatsResp>) => {
        state.loading = false;
        state.createdCategories = !!action.payload.success;

        // Find the category by ID and update it in the state
      }
    );

    // Handle the rejected state for createCategories
    builder.addCase(createCategories.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) ?? action.error?.message ?? null;
      state.createdCategories = false;
    });

    builder.addCase(getCategoryByCategoryId.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.createdCategories = false;
    });

    // Handle the fulfilled state for getCategoryByCategoryId
    builder.addCase(
      getCategoryByCategoryId.fulfilled,
      (state, action: PayloadAction<Category[]>) => {
        state.loading = false;
        state.categories = action.payload;

        // Find the category by ID and update it in the state
      }
    );

    // Handle the rejected state for getCategoryByCategoryId
    builder.addCase(getCategoryByCategoryId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.createdCategories = false;
    });
  },
});

export const categorySliceReducer = categorySlice.reducer;
